import {
  ChangeEvent,
  FormEventHandler,
  FunctionComponent,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import styled, { css, useTheme } from 'styled-components'

import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import translate from '@i18n'
import useExecuteOnClientNavigation from '@hooks/useExecuteOnClientNavigation'
import IconButton from '@components/Buttons/IconButton'

import { QuestionType } from './types'

const MAX_TEXTAREA_HEIGHT = 162
const MIN_TEXTAREA_HEIGHT = 42

type AISearchInputProps = {
  answerIsLoading: boolean
  onQuestionSubmit: (question: QuestionType) => void
}

const StyledForm = styled.form`
  ${({
    theme: {
      spacing: { spacing12, spacing16 },
      colors: { fillSurface, strokeWeak },
      measures: { stretchWidth, outerPadding },
    },
  }) => css`
    position: fixed;
    display: flex;
    gap: ${spacing12};
    align-items: flex-end;
    bottom: 0;
    z-index: 2;
    padding: ${spacing16};
    background-color: ${fillSurface};
    box-sizing: border-box;

    ${stretchWidth()};

    ${desktopCSS(css`
      width: 994px;
    `)};

    ${tabletCSS(css`
      width: 768px;
      padding-left: ${outerPadding.tablet};
      padding-right: ${outerPadding.tablet};
    `)};

    ${mobileCSS(css`
      width: 100%;
      border-top: 1px solid ${strokeWeak};
    `)};
  `}
`

const StyledInputWrapper = styled.div<{ isFocused: boolean }>`
  ${({
    theme: {
      colors: { fillDefault, strokeDefault },
      spacing: { spacing16, spacing24 },
    },
    isFocused,
  }) => css`
    display: flex;
    align-items: center;
    border: 1px solid ${strokeDefault};
    margin: 1px;
    border-radius: ${spacing24};
    padding: 0 ${spacing16};
    min-height: calc(48px - 2px);
    flex: 1;
    background-color: ${fillDefault};

    ${isFocused &&
    css`
      border: 2px solid ${strokeDefault};
      margin: 0;
    `}
  `}
`

const StyledTextArea = styled.textarea`
  ${({
    theme: {
      typography: {
        bodycopy: {
          small2: { bundledCSS: small2BodyCopyCSS },
        },
      },
      colors: { fillDefault, textWeak, textDefault },
      spacing: { spacing8 },
    },
  }) => css`
    height: 32px;
    width: 100%;
    border: 0;
    padding: 0;
    color: ${textDefault};
    background: ${fillDefault};
    box-sizing: border-box;
    resize: none;
    border-radius: 5px;
    ${small2BodyCopyCSS};
    padding: ${spacing8} 0 ${spacing8} 0;
    margin: 0;
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 transparent;
    &::placeholder {
      ${small2BodyCopyCSS};
      color: ${textWeak};
    }
    &:focus {
      outline: none;
    }
  `}
`

const StyledSubmitButton = styled(IconButton)<{ isFocused: boolean }>`
  ${({
    theme: {
      colors: { fillStrong, fillStrongHover, fillInverse, fillInverseHover },
    },
    isFocused,
  }) => css`
    background: ${fillStrong};
    border: 0;
    cursor: pointer;
    height: 48px;
    width: 48px;
    min-width: 48px;
    border-radius: 48px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: ${fillStrongHover};
    }

    &:disabled {
      pointer-events: none;
    }

    ${isFocused &&
    css`
      background: ${fillInverse};

      &:hover {
        background: ${fillInverseHover};
      }
    `}
  `}
`

const AISearchInput: FunctionComponent<AISearchInputProps> = ({
  onQuestionSubmit,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const submitRef = useRef<HTMLFormElement>(null)
  const [value, setValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  const theme = useTheme()

  const onInputValueChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    setValue(event.target.value)

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    const question = textAreaRef?.current?.value

    if (question) {
      onQuestionSubmit({
        id: `user-question-${new Date().getTime()}`,
        text: question,
        isCached: false,
        isSuggestedQuestion: false,
        context: 'home',
      })
    }
  }

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = `${MIN_TEXTAREA_HEIGHT}px`
      const scrollHeight = textAreaRef.current.scrollHeight
      // scrollHeight + 1 -> workaround for the issue with the scrollbar in the Samsung browser
      // (scrollbar shows even if there is nowhere to scroll to)
      textAreaRef.current.style.height = `${Math.min(Math.max(scrollHeight + 1, MIN_TEXTAREA_HEIGHT), MAX_TEXTAREA_HEIGHT)}px`
    }
  }, [value])

  const resetInputState = useCallback(() => {
    setValue('')
    setIsFocused(false)
    if (textAreaRef.current) {
      textAreaRef.current.value = ''
      textAreaRef.current.style.height = `${MIN_TEXTAREA_HEIGHT}px`
    }
  }, [])

  useExecuteOnClientNavigation(resetInputState)

  const onTextAreaKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (
    event,
  ) => {
    if (
      (event?.key === 'Enter' && !event.shiftKey) ||
      (event?.keyCode === 13 && !event.shiftKey)
    ) {
      event.preventDefault()
      submitRef.current?.requestSubmit()
    }
  }

  const onTextAreaFocus = () => {
    setIsFocused(true)
  }

  const onTextAreaBlur = () => {
    setIsFocused(false)
  }

  return (
    <StyledForm ref={submitRef} onSubmit={onSubmit}>
      <StyledInputWrapper isFocused={isFocused}>
        <StyledTextArea
          name="ai-search-input"
          maxLength={750}
          placeholder={translate('aiSearch.inputPlaceholder')}
          ref={textAreaRef}
          value={value}
          onChange={onInputValueChange}
          onKeyDown={onTextAreaKeyDown}
          onFocus={onTextAreaFocus}
          onBlur={onTextAreaBlur}
        />
      </StyledInputWrapper>
      <StyledSubmitButton
        type="submit"
        iconName="arrow-up"
        iconSize="24px"
        iconColor={
          isFocused ? theme.colors.iconInverse : theme.colors.iconDefault
        }
        ariaLabel="Submit question"
        isFocused={isFocused}
      />
    </StyledForm>
  )
}

export default AISearchInput
